/**
 * Prototyper Types
 *
 * Types for prototype creation and management
 */

/**
 * LLM used for WebContainer build-stream (and legacy POST /prototype when applicable).
 */
export interface PrototyperGenerationModel {
  label: string;
  modelProvider: string;
  modelChoice: string;
}

export const PROTOTYPER_GENERATION_MODELS: readonly PrototyperGenerationModel[] = [
  // { label: 'GLM 5', modelProvider: 'zai', modelChoice: 'glm-5' },
  { label: 'gemini-3.7-flash', modelProvider: 'Google', modelChoice: 'gemini-3.7-flash' },
  { label: 'Gemini 3 Flash', modelProvider: 'Google', modelChoice: 'gemini-3-flash-preview' },
  { label: 'Mimo V2 Pro', modelProvider: 'xiaomi', modelChoice: 'mimo-v2-pro' },
  // { label: 'Kimi K2.5 Thinking', modelProvider: 'moonshotai', modelChoice: 'kimi-k2.5' },
] as const;

export const DEFAULT_PROTOTYPER_GENERATION_MODEL: PrototyperGenerationModel =
  PROTOTYPER_GENERATION_MODELS[0];

/**
 * Parameters for creating a prototype
 */
export interface PrototypeCreationParams {
  conversationId: string;
  projectId?: string | null;
  modelProvider: string;
  modelChoice: string;
  signal?: AbortSignal;
}

/**
 * Response from prototype creation API
 */
/**
 * One version published under a prototype's link, reachable at `/p/<slug>/v<number>`.
 * `number` is the version's position among its prototype's versions.
 */
export interface PublishedPrototypeVersion {
  versionId: string;
  number: number;
  publishedAt: string;
}

export interface PrototypeCreationResponse {
  prototype_id?: string;
  prototype_url?: string;
  demo_url?: string;
  demoUrl?: string;
  prototyper_demo_link_id?: number;
  isNewlyGenerated?: boolean;
  id?: number;
  slug?: string | null;
  header?: string | null;
  conversationId?: string;
  projectId?: string;
  status: 'success' | 'error';
  message?: string;
  data?: any;
  /** The prototype this link publishes (one link per prototype). */
  prototypeId?: string | null;
  /** The version the main link serves; null when unpublished or not recorded. */
  publishedVersionId?: string | null;
  publishedVersions?: PublishedPrototypeVersion[];
}

/**
 * Prototyper state
 */
export interface PrototyperState {
  isCreatingPrototype: boolean;
  prototypeError: string | null;
  prototypeUrl: string | null;
  isNewlyGenerated?: boolean;
}

/**
 * Parameters for updating a prototyper demo link
 */
export interface UpdatePrototyperDemoLinkParams {
  id: number;
  slug?: string | null;
}

/**
 * Response from fetching prototyper by slug
 */
export interface PrototyperBySlugResponse {
  id: string;
  llmInteractionId: string;
  tmpLlmInteractionId: string;
  demoUrl: string;
  createdAt: string;
  slug: string;
  header: string;
}

/** Nest: POST …/prototype/snapshot */
export interface PrototypeSnapshotPrepareResponse {
  snapshotId: string;
  revision: number;
  uploadUrl: string;
  expiresIn: number;
  bucket: string;
  key: string;
  maxBytes: number;
}

export interface PrototypeSnapshotFinalizeResponse {
  snapshotId: string;
  revision: number;
  status: string;
}

export interface PrototypeSnapshotLatestResponse {
  snapshotId: string;
  revision: number;
  downloadUrl: string;
  expiresIn: number;
  etag?: string | null;
  sizeBytes?: string | null;
  createdAt: string;
  /** Originating prototyper assistant turn, when persisted by Nest. */
  llmInteractionId?: string | null;
  /** True when the snapshot was finalized after a successful production build. */
  buildVerified?: boolean;
}

export interface PrototypePublishResponse {
  previewUrl: string;
  slug: string;
  prototyperDemoLinkId: string;
  deployedAt: string;
  prototypeId?: string | null;
  publishedVersionId?: string | null;
  publishedVersions?: PublishedPrototypeVersion[];
}

// ── Prototype versions (backend `src/modules/prototyper/versions`, plan P6.1 / P6.4) ──

export type PrototypeVersionStatus =
  | 'draft'
  | 'verified'
  | 'failed'
  | 'failed_needs_input'
  | 'discarded'
  | 'superseded';

export interface PrototypeVersion {
  id: string;
  llmInteractionId: string | null;
  prototypeId: string | null;
  number: number | null;
  status: PrototypeVersionStatus;
  buildStatus: string;
  contentHash: string | null;
  snapshotId: string | null;
  turnKind: string | null;
  fixAttempts: number;
  fixAttemptsGranted: number;
  lastErrorKind: string | null;
  lastErrorSignature: string | null;
  leaseOwner: string | null;
  leaseExpiresAt: string | null;
  hasBuildLog: boolean;
  createdAt: string;
  updatedAt: string;
  /** Whether another automatic fix attempt may start (the server's rule). */
  fixAllowed?: boolean;
}

/** A turn's version with its prototype's Chef project (`GET …/prototype/versions`, P6.4c). */
export interface PrototypeTurnVersion extends PrototypeVersion {
  chefProjectId: string | null;
}

/**
 * The verdict from building a version's saved files on the builder
 * (`POST …/versions/:id/build`, light-preview plan F1).
 *
 * `environmentFailure` is the builder itself failing — it could not install or set up, whatever
 * the code says — and leaves the version untouched, so it must never be answered with a fix.
 */
export interface PrototypeVersionBuildResult {
  built: boolean;
  environmentFailure: boolean;
  status: PrototypeVersionStatus;
  /** Whether another automatic fix may start, when the build failed on the code. */
  fixAllowed?: boolean;
  /** Build output; empty on success. */
  errors: string;
}

/** A draft's files as the builder saved them (`GET …/versions/:id/files`, P6.4d-3). */
export interface PrototypeVersionFiles {
  versionId: string;
  files: Record<string, string>;
}

export interface PrototypeVersionDetail extends PrototypeVersion {
  buildLog: string | null;
}

export interface ConversationPrototypeWithVersions {
  id: string;
  title: string | null;
  summary: string | null;
  currentVersionId: string | null;
  currentVersion: PrototypeVersion | null;
  latestVersion: PrototypeVersion | null;
}

export interface PrototypeVersionFailResult {
  status: PrototypeVersionStatus;
  fixAllowed: boolean;
  fixAttempts: number;
  fixAttemptsGranted: number;
}

export interface PrototypeVersionFixAttemptResult {
  allowed: boolean;
  status: PrototypeVersionStatus;
  fixAttempts: number;
  fixAttemptsGranted: number;
}

/**
 * Canonical prototype resource from GET `/prototypes` (chef_project_links).
 * Homogeneous collection — not mixed into `/artifacts`.
 */
export interface Prototype {
  id: string;
  title: string | null;
  summary: string | null;
  conversationId: string;
  projectId: string;
  scopeId: string | null;
  lastBuiltInteractionId: string | null;
  createdAt?: string;
  updatedAt?: string | null;
}
